import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { UserLabel } from '@webapp/core/components/UserLabel'
import { useProcurifyIntl } from '@webapp/hooks'
import { CardholderSelectSelectedValueStyled } from '@webapp/modules/spending-cards/components/CardholderSelect/styles/CardholderSelectSelectedValueStyled'
import { type CardholderAccountStatusEnum } from '@webapp/modules/spending-cards/entities/enums/CardholderAccountStatusEnum'
import messages from './messages'
import { cardholderRepository } from '../../data-access/repositories/CardholderRepository'
import { Cardholder } from '../../entities/Cardholder'

const SelectedValue = (props: SingleValueProps<Cardholder, false>) => {
  const { data } = props

  return (
    <ReactSelectComponents.SingleValue {...props}>
      <CardholderSelectSelectedValueStyled>
        <UserLabel
          fluid={true}
          user={{ fullName: data.fullName, profileImage: data.profileImage }}
          WithUserTooltipProps={{
            disabled: true,
          }}
        />
      </CardholderSelectSelectedValueStyled>
    </ReactSelectComponents.SingleValue>
  )
}

const SelectOption = (props: OptionProps<Cardholder, false>) => {
  const { data } = props

  return (
    <ReactSelectComponents.Option {...props}>
      <UserLabel
        fluid={true}
        user={{ fullName: data.fullName, profileImage: data.profileImage }}
      />
    </ReactSelectComponents.Option>
  )
}

export interface ICardholderSelectProps extends IAsyncSelectProps<Cardholder> {
  cardholderStatus?: CardholderAccountStatusEnum
}

export const CardholderSelect = ({ ...props }: ICardholderSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions: IAsyncSelectProps<Cardholder>['loadOptions'] = async (
    inputValue,
    pagination,
    callback
  ) => {
    const statusParam = props.cardholderStatus
      ? { status: props.cardholderStatus }
      : {}

    const res = await cardholderRepository.getCardholders({
      search: inputValue,
      page: pagination.page,
      ...statusParam,
    })

    callback({
      options: res.data.map((cardholder) => new Cardholder(cardholder)),
      pagination: {
        hasMore: res.pagination
          ? res.pagination?.page < res.pagination?.numPages
          : false,
        page: pagination.page + 1,
      },
    })
  }

  return (
    <AsyncSelect<Cardholder>
      cacheOptions={true}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      components={{
        SingleValue: SelectedValue,
        Option: SelectOption,
      }}
      getOptionLabel={(cardholder) => cardholder.fullName}
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
